import React, { useState, useEffect, useCallback } from 'react';
import { Compass, Smartphone, Lock, Unlock, RotateCcw, AlertCircle, Check } from 'lucide-react';

/**
 * DeviceCompass - Uses device's actual compass/magnetometer to detect north
 * 
 * Features:
 * - Real-time compass heading from device
 * - Permission handling for iOS 13+
 * - Lock direction to set floor plan orientation
 * - Fallback for unsupported devices
 */

const DeviceCompass = ({ 
  onOrientationDetected, 
  onLockDirection,
  size = 'medium' 
}) => {
  const [heading, setHeading] = useState(null);
  const [permission, setPermission] = useState('unknown'); // unknown, granted, denied, unsupported
  const [isLocked, setIsLocked] = useState(false);
  const [lockedHeading, setLockedHeading] = useState(null);
  const [error, setError] = useState(null);
  const [isCalibrating, setIsCalibrating] = useState(true);
  const [headingHistory, setHeadingHistory] = useState([]);

  const sizeClasses = {
    small: 'w-32 h-32',
    medium: 'w-48 h-48',
    large: 'w-64 h-64'
  };

  // Convert heading to cardinal direction
  const getCardinalDirection = (deg) => {
    if (deg === null) return 'N';
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(deg / 45) % 8;
    return directions[index];
  };

  // Get direction name
  const getDirectionName = (dir) => {
    const names = {
      'N': 'North', 'NE': 'Northeast', 'E': 'East', 'SE': 'Southeast',
      'S': 'South', 'SW': 'Southwest', 'W': 'West', 'NW': 'Northwest'
    };
    return names[dir] || dir;
  };

  // Smooth the heading using moving average
  const smoothHeading = useCallback((newHeading) => {
    setHeadingHistory(prev => {
      const history = [...prev, newHeading].slice(-10); // Keep last 10 readings
      const avg = history.reduce((a, b) => a + b, 0) / history.length;
      return history;
    });
  }, []);

  // Handle device orientation event
  const handleOrientation = useCallback((event) => {
    let compassHeading = null;

    // iOS provides webkitCompassHeading
    if (event.webkitCompassHeading !== undefined) {
      compassHeading = event.webkitCompassHeading;
    } 
    // Android/other - calculate from alpha
    else if (event.alpha !== null) {
      // Alpha is the compass direction the device is facing
      // 0 = North, 90 = East, 180 = South, 270 = West
      compassHeading = 360 - event.alpha;
      if (compassHeading < 0) compassHeading += 360;
      if (compassHeading >= 360) compassHeading -= 360;
    }

    if (compassHeading !== null && !isNaN(compassHeading)) {
      smoothHeading(compassHeading);
      
      // Calculate smoothed heading
      if (headingHistory.length > 0) {
        const smoothedHeading = headingHistory.reduce((a, b) => a + b, 0) / headingHistory.length;
        setHeading(Math.round(smoothedHeading));
        
        // Stop calibrating after a few readings
        if (headingHistory.length >= 5) {
          setIsCalibrating(false);
        }
      } else {
        setHeading(Math.round(compassHeading));
      }

      if (onOrientationDetected && !isLocked) {
        onOrientationDetected({
          heading: Math.round(compassHeading),
          direction: getCardinalDirection(compassHeading)
        });
      }
    }
  }, [isLocked, onOrientationDetected, smoothHeading, headingHistory]);

  // Request permission and start listening
  const requestPermission = async () => {
    setError(null);
    
    // Check if DeviceOrientationEvent is available
    if (!window.DeviceOrientationEvent) {
      setPermission('unsupported');
      setError('Device orientation not supported on this device');
      return;
    }

    // iOS 13+ requires permission request
    if (typeof DeviceOrientationEvent.requestPermission === 'function') {
      try {
        const permissionState = await DeviceOrientationEvent.requestPermission();
        if (permissionState === 'granted') {
          setPermission('granted');
          window.addEventListener('deviceorientation', handleOrientation, true);
        } else {
          setPermission('denied');
          setError('Permission denied. Please enable in Settings.');
        }
      } catch (err) {
        setPermission('denied');
        setError('Failed to request permission: ' + err.message);
      }
    } else {
      // Non-iOS or older iOS - just start listening
      setPermission('granted');
      window.addEventListener('deviceorientation', handleOrientation, true);
      
      // Check if we're getting data after a short delay
      setTimeout(() => {
        if (heading === null) {
          setPermission('unsupported');
          setError('No compass data received. Device may not have a magnetometer.');
        }
      }, 3000);
    }
  };

  // Lock current direction
  const toggleLock = () => {
    if (!isLocked && heading !== null) {
      setLockedHeading(heading);
      setIsLocked(true);
      if (onLockDirection) {
        onLockDirection({
          heading: heading,
          direction: getCardinalDirection(heading)
        });
      }
    } else {
      setIsLocked(false);
      setLockedHeading(null);
    }
  };

  // Reset compass
  const resetCompass = () => {
    setIsLocked(false);
    setLockedHeading(null);
    setHeadingHistory([]);
    setIsCalibrating(true);
  };

  // Cleanup
  useEffect(() => {
    return () => {
      window.removeEventListener('deviceorientation', handleOrientation, true);
    };
  }, [handleOrientation]);

  // Auto-start on mount for non-iOS
  useEffect(() => {
    if (typeof DeviceOrientationEvent.requestPermission !== 'function') {
      // Non-iOS - try to start automatically
      if (window.DeviceOrientationEvent) {
        setPermission('granted');
        window.addEventListener('deviceorientation', handleOrientation, true);
      }
    }
  }, []);

  const displayHeading = isLocked ? lockedHeading : heading;
  const displayDirection = getCardinalDirection(displayHeading);

  return (
    <div className="flex flex-col items-center p-4 bg-white rounded-2xl shadow-lg">
      {/* Title */}
      <div className="flex items-center gap-2 mb-4">
        <Smartphone className="w-5 h-5 text-[#232323]" />
        <h3 className="font-bold text-[#232323]">Device Compass</h3>
      </div>

      {/* Permission Request */}
      {permission === 'unknown' && (
        <div className="text-center mb-4">
          <p className="text-sm text-gray-600 mb-4">
            Use your phone's compass to detect the actual north direction
          </p>
          <button
            onClick={requestPermission}
            className="px-6 py-3 bg-[#232323] text-white rounded-xl font-semibold 
                       hover:bg-[#353535] transition-all flex items-center gap-2 mx-auto"
          >
            <Compass className="w-5 h-5" />
            Enable Compass
          </button>
        </div>
      )}

      {/* Unsupported */}
      {permission === 'unsupported' && (
        <div className="text-center p-4 bg-orange-50 rounded-xl">
          <AlertCircle className="w-8 h-8 text-orange-500 mx-auto mb-2" />
          <p className="text-sm text-orange-700">{error || 'Compass not available'}</p>
          <p className="text-xs text-orange-600 mt-2">
            Please select direction manually below
          </p>
        </div>
      )}

      {/* Denied */}
      {permission === 'denied' && (
        <div className="text-center p-4 bg-red-50 rounded-xl">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <p className="text-sm text-red-700">{error}</p>
          <button
            onClick={requestPermission}
            className="mt-3 px-4 py-2 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Compass Display */}
      {permission === 'granted' && (
        <>
          {/* Compass Rose */}
          <div className={`relative ${sizeClasses[size]} mb-4`}>
            <svg 
              viewBox="0 0 200 200" 
              className="w-full h-full"
              style={{ 
                transform: `rotate(${-(displayHeading || 0)}deg)`,
                transition: isLocked ? 'none' : 'transform 0.1s ease-out'
              }}
            >
              {/* Outer ring */}
              <circle cx="100" cy="100" r="95" fill="#f8f9fa" stroke="#e5e7eb" strokeWidth="2" />
              
              {/* Degree marks */}
              {[...Array(36)].map((_, i) => {
                const angle = i * 10;
                const isCardinal = angle % 90 === 0;
                const isIntercardinal = angle % 45 === 0 && !isCardinal;
                const length = isCardinal ? 15 : isIntercardinal ? 10 : 5;
                const rad = (angle - 90) * Math.PI / 180;
                const x1 = 100 + 85 * Math.cos(rad);
                const y1 = 100 + 85 * Math.sin(rad);
                const x2 = 100 + (85 - length) * Math.cos(rad);
                const y2 = 100 + (85 - length) * Math.sin(rad);
                return (
                  <line 
                    key={i} 
                    x1={x1} y1={y1} x2={x2} y2={y2} 
                    stroke={isCardinal ? '#232323' : '#9CA3AF'} 
                    strokeWidth={isCardinal ? 2 : 1} 
                  />
                );
              })}
              
              {/* Cardinal labels */}
              <text x="100" y="25" textAnchor="middle" fill="#EF4444" fontSize="16" fontWeight="bold">N</text>
              <text x="175" y="104" textAnchor="middle" fill="#232323" fontSize="14" fontWeight="bold">E</text>
              <text x="100" y="185" textAnchor="middle" fill="#232323" fontSize="14" fontWeight="bold">S</text>
              <text x="25" y="104" textAnchor="middle" fill="#232323" fontSize="14" fontWeight="bold">W</text>
              
              {/* North pointer (red) */}
              <polygon points="100,30 90,70 100,60 110,70" fill="#EF4444" />
              {/* South pointer (white) */}
              <polygon points="100,170 90,130 100,140 110,130" fill="#9CA3AF" />
            </svg>

            {/* Center indicator (fixed, points to device direction) */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-16 h-16 rounded-full bg-white shadow-lg flex items-center justify-center border-4 border-[#232323]">
                <div className="text-center">
                  <div className="text-lg font-bold text-[#232323]">{displayHeading || 0}°</div>
                  <div className="text-xs text-gray-500">{displayDirection}</div>
                </div>
              </div>
            </div>

            {/* Device direction indicator (triangle at top, fixed position) */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2">
              <div className="w-4 h-4 bg-[#232323] rotate-45 transform origin-center"></div>
            </div>

            {/* Lock indicator */}
            {isLocked && (
              <div className="absolute top-2 right-2 bg-green-500 rounded-full p-1">
                <Lock className="w-4 h-4 text-white" />
              </div>
            )}

            {/* Calibrating indicator */}
            {isCalibrating && !isLocked && (
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-yellow-100 text-yellow-700 
                              text-xs px-2 py-1 rounded-full animate-pulse">
                Calibrating...
              </div>
            )}
          </div>

          {/* Direction Display */}
          <div className="text-center mb-4">
            <p className="text-2xl font-bold text-[#232323]">
              {getDirectionName(displayDirection)}
            </p>
            <p className="text-sm text-gray-500">
              {isLocked ? 'Direction locked' : 'Point your phone to the entrance'}
            </p>
          </div>

          {/* Controls */}
          <div className="flex gap-3">
            <button
              onClick={toggleLock}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
                isLocked 
                  ? 'bg-green-500 text-white hover:bg-green-600' 
                  : 'bg-[#232323] text-white hover:bg-[#353535]'
              }`}
            >
              {isLocked ? (
                <>
                  <Unlock className="w-4 h-4" />
                  Unlock
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4" />
                  Lock Direction
                </>
              )}
            </button>
            
            <button
              onClick={resetCompass}
              className="flex items-center gap-2 px-4 py-2 border-2 border-gray-300 
                         rounded-lg text-gray-600 hover:border-[#232323] hover:text-[#232323]"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </button>
          </div>

          {/* Instructions */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
            <p className="font-semibold mb-1">📱 How to use:</p>
            <ol className="list-decimal list-inside text-xs space-y-1">
              <li>Hold your phone flat, parallel to the ground</li>
              <li>Point the top of your phone towards your main entrance</li>
              <li>Wait for the compass to stabilize</li>
              <li>Tap "Lock Direction" to set your floor plan orientation</li>
            </ol>
          </div>

          {/* Locked confirmation */}
          {isLocked && (
            <div className="mt-3 flex items-center gap-2 text-green-600 bg-green-50 px-4 py-2 rounded-lg">
              <Check className="w-5 h-5" />
              <span className="font-semibold">
                Entrance faces {getDirectionName(displayDirection)} ({lockedHeading}°)
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DeviceCompass;
